import { useState } from 'react';
import { IconMailForward } from '@tabler/icons-react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';
import { getNetworkError } from 'common/utils';
import { AlertMessage, Button, ConfirmationModal } from 'ui';

import { resendAnnualSummary } from '../../api/donations';

/**
 * Emails one contact their previous-year donation summary — the same email the yearly
 * annual summary run sends. The year is decided by the server; `year` here is only for copy.
 */
const SendAnnualSummaryButton = ({ contactId, email, year }) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const handleConfirm = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await resendAnnualSummary(contactId);
      setSuccess(`${result?.year ?? year} donation summary sent to ${result?.email ?? email}.`);
      setConfirmOpen(false);
    } catch (err) {
      Sentry.captureException(err);
      setError(getNetworkError(err));
      setConfirmOpen(false);
    }
    setLoading(false);
  };

  return (
    <>
      <Button
        title={`Email ${year} summary`}
        size="sm"
        LeftIcon={IconMailForward}
        iconProps={{ size: 16 }}
        onClick={() => setConfirmOpen(true)}
        data-testid="send-annual-summary-button"
      />

      <ConfirmationModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
        loading={loading}
        title={`Email ${year} Donation Summary`}
        message={
          <>
            Send the {year} donation summary to <span className="sentry-mask font-semibold">{email}</span>?
          </>
        }
      />

      <AlertMessage open={!!success} message={success} severity="success" onClose={() => setSuccess('')} />
      <AlertMessage open={!!error} message={error} severity="error" onClose={() => setError('')} />
    </>
  );
};

SendAnnualSummaryButton.propTypes = {
  contactId: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  year: PropTypes.number.isRequired,
};

export default SendAnnualSummaryButton;
